import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getStudentData, getGroupsData } from '../assets/data.js';
import { getStudents as getStudentsFromSupabase } from '../utils/studentsService';
import { getGroups } from '../utils/groupsService';
import { getSchedule, saveScheduleItem } from '../utils/scheduleService';
import { saveLesson, getLessonsFromLocalStorage, saveLessonsToLocalStorage } from '../utils/lessonsService';
import { supabase } from '../utils/supabaseClient';
import { getCurrentUser } from '../utils/supabaseClient';
import { TEMPLATE_LESSONS } from '../data/templateLessons';
import Tour from '../components/Tour/Tour';
import TourTrigger from '../components/Tour/TourTrigger';
import { HOME_TOUR_STEPS } from '../components/Tour/tourSteps';
import './Home.css';

const HOME_TOUR_SEEN_KEY = 'novas_tour_home_v1_seen';

const NEWS_ITEMS = [
  {
    id: 19,
    date: 'May 21, 2026',
    tag: 'Fix',
    tagColor: '#10b981',
    title: 'Whiteboard exercises: isStudent view, position & sync',
    text: 'Exercises on the board now show the correct student view. New exercises insert at the current viewport center instead of the top-left corner. Open/close state syncs in real time between teacher and student boards.',
  },
  {
    id: 18,
    date: 'May 21, 2026',
    tag: 'Fix',
    tagColor: '#10b981',
    title: 'Pros & Cons board: expanded view no longer crashes',
    text: 'Fixed crash when opening a Pros & Cons exercise on the whiteboard. Also corrected wrong data keys (topic/cefr) that caused the preview to show default values.',
  },
  {
    id: 17,
    date: 'May 21, 2026',
    tag: 'Fix',
    tagColor: '#10b981',
    title: 'Whiteboard: exercises insert at viewport center',
    text: 'Exercises dragged from the lesson panel to the whiteboard now appear in the center of the current view instead of always at the top-left corner.',
  },
  {
    id: 16,
    date: 'May 21, 2026',
    tag: 'New',
    tagColor: '#6366f1',
    title: 'Pros & Cons: open/close sync + student view',
    text: 'Exercise opens and closes simultaneously. "How to use" and "Add discussion questions" are hidden from students. Teacher generates questions privately, then clicks "Show to Student" to reveal them in real time.',
  },
  {
    id: 15,
    date: 'May 21, 2026',
    tag: 'New',
    tagColor: '#6366f1',
    title: 'Quiz: full real-time sync',
    text: 'Quiz opens and closes simultaneously. Answer selections appear live on both screens. Navigating between questions (prev/next/dots) syncs both participants to the same question. Submit and Try Again sync both sides.',
  },
  {
    id: 14,
    date: 'May 21, 2026',
    tag: 'New',
    tagColor: '#6366f1',
    title: 'Error Correction: full real-time sync',
    text: 'Exercise opens and closes simultaneously for both participants. Typed answers appear live on both screens. Per-sentence correct/incorrect feedback syncs instantly. Finish and Try Again sync the results panel. Added "Press ↵" hint inside each input.',
  },
  {
    id: 13,
    date: 'May 21, 2026',
    tag: 'Fix',
    tagColor: '#f59e0b',
    title: 'Video call: VPN / Russia fix',
    text: 'Replaced blocked Google STUN with Cloudflare & Twilio STUN. Added TLS TURN relay (turns: port 443) which passes through most VPNs. If ICE fails, a "Relay mode" button appears to retry with TURN-only routing.',
  },
  {
    id: 12,
    date: 'May 21, 2026',
    tag: 'New',
    tagColor: '#6366f1',
    title: 'Video call: focus mode & size sync',
    text: 'Cycle through small → medium → focus mode (fullscreen blur overlay with large circles). Focus mode hides editor UI. Size mode syncs to the other participant automatically.',
  },
  {
    id: 11,
    date: 'May 21, 2026',
    tag: 'New',
    tagColor: '#6366f1',
    title: 'Word Translation: full real-time sync',
    text: 'Both sides now share the same shuffle order, see each other\'s selections and highlights in real time, and get results simultaneously. Try Again resets both sides. Language badge now matches the level badge style.',
  },
  {
    id: 10,
    date: 'May 21, 2026',
    tag: 'Fix',
    tagColor: '#f59e0b',
    title: 'YouTube sync: play, pause & seek now work for both sides',
    text: 'Fixed a missing props bug in the active YouTube rendering block — onPlayPause, onSeek, and currentTime were never wired up, so sync was silently broken. All three now work bidirectionally.',
  },
  {
    id: 9,
    date: 'May 21, 2026',
    tag: 'New',
    tagColor: '#6366f1',
    title: 'Media sync: video & audio play/pause/seek synced',
    text: 'When teacher or student plays, pauses, or scrubs a video or audio file, the other side\'s player mirrors the action in real time.',
  },
  {
    id: 8,
    date: 'May 21, 2026',
    tag: 'New',
    tagColor: '#6366f1',
    title: 'Media files visible to both participants',
    text: 'Dropped video and audio files are now uploaded to Supabase Storage on insert, replacing local blob URLs with permanent https:// links that both teacher and student can load.',
  },
  {
    id: 7,
    date: 'May 21, 2026',
    tag: 'New',
    tagColor: '#6366f1',
    title: 'Bidirectional whiteboard sync & multiple fixes',
    text: 'Students can now draw and edit on the whiteboard and the teacher sees it live. Fixed: drawing over images, moving and resizing pencil strokes, YouTube/video URL paste/drop detection, and element flickering on insert.',
  },
  {
    id: 1,
    date: 'May 19, 2025',
    tag: 'New',
    tagColor: '#6366f1',
    title: 'Real-time sync for Matching Halves',
    text: 'Both teacher and student now see the same column layout, selections, correct/incorrect flashes, and matched pairs in real time. Try Again resets both sides with a fresh shuffle.',
  },
  {
    id: 2,
    date: 'May 19, 2025',
    tag: 'Fix',
    tagColor: '#f59e0b',
    title: 'ABCD exercise: score and coloring fixed',
    text: 'Teacher submitting answers now shows the correct score and colored options. Edit mode no longer cross-contaminates questions with non-unique IDs generated by AI.',
  },
  {
    id: 3,
    date: 'May 19, 2025',
    tag: 'New',
    tagColor: '#6366f1',
    title: 'ABCD: correct answer hint for teacher',
    text: 'A small "Correct answer: B. …" hint is now shown under the options list, visible only to the teacher during a lesson.',
  },
  {
    id: 4,
    date: 'May 18, 2025',
    tag: 'New',
    tagColor: '#6366f1',
    title: 'Open Questions: real-time typing & sync',
    text: 'Student\'s typed answers appear live in the teacher\'s view. Navigation, Try Again, and AI review results are all synced between sides. Suggested answers are now teacher-only.',
  },
  {
    id: 5,
    date: 'May 18, 2025',
    tag: 'New',
    tagColor: '#6366f1',
    title: 'True/False: explanations & homework mode',
    text: 'Teacher always sees explanations. "Show to student" checkbox reveals the explanation live on the student side. "Always show answers" option in edit mode enables explanations in homework without a teacher present.',
  },
  {
    id: 6,
    date: 'May 18, 2025',
    tag: 'Improvement',
    tagColor: '#10b981',
    title: 'True/False & ABCD full real-time sync',
    text: 'Answer selection, navigation, Show Results, and Try Again are now fully synced between teacher and student across all exercise types.',
  },
];

// Кэш блока Classes (upcoming events): не перезагружать при каждом заходе на Home
const HOME_EVENTS_CACHE_TTL_MS = 90 * 1000;
const homeEventsCache = { events: [], lastFetch: 0 };

export default function Home() {
  const navigate = useNavigate();
  const [todayEvents, setTodayEvents] = useState([]);
  const [classesLoading, setClassesLoading] = useState(true);
  const [monthlyEarnings, setMonthlyEarnings] = useState(0);
  const [plannedIncome, setPlannedIncome] = useState(0);

  // Template lessons
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(null);

  // Student selection modal state
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [openStatusMenuId, setOpenStatusMenuId] = useState(null);
  const [magicFeatureToast, setMagicFeatureToast] = useState(false);
  const magicToastTimeoutRef = useRef(null);
  const statusWrapRef = useRef(null);
  const bannerVideoRef = useRef(null);
  const teacherIdRef = useRef(null);

  // Refs для хранения актуальных версий функций
  const calculateMonthlyIncomeRef = useRef(null);
  const calculateLessonsCountRef = useRef(null);
  const loadUpcomingEventsRef = useRef(null);

  // Onboarding tour state — opens automatically on first visit, then
  // re-runnable via the floating "Quick Tour" button.
  const [tourOpen, setTourOpen] = useState(false);
  useEffect(() => {
    try {
      if (!localStorage.getItem(HOME_TOUR_SEEN_KEY)) {
        // Slight delay so the page mounts and stats render before we
        // try to locate target elements.
        const t = setTimeout(() => setTourOpen(true), 700);
        return () => clearTimeout(t);
      }
    } catch (_) {}
  }, []);

  const UPCOMING_EVENTS_LIMIT = 5;

  // Load nearest N upcoming events. useCacheIfValid = true — взять из кэша при переходе на вкладку.
  const loadUpcomingEvents = async (silent = false, useCacheIfValid = false) => {
    const now = Date.now();
    const cacheValid = (now - homeEventsCache.lastFetch < HOME_EVENTS_CACHE_TTL_MS) && homeEventsCache.events.length >= 0;

    if (useCacheIfValid && cacheValid) {
      setTodayEvents(homeEventsCache.events);
      setClassesLoading(false);
      return;
    }

    if (!silent) setClassesLoading(true);
    try {
      const allLessons = await getSchedule();
      const nowDate = new Date();

      const upcoming = (allLessons || [])
        .filter(lesson => {
          if (!lesson.date) return false;
          const lessonDate = new Date(lesson.date);
          const from = (lesson.timeFrom || lesson.from || '00:00').toString().replace(/:/g, '').slice(0, 4);
          const h = parseInt(from.slice(0, 2), 10) || 0;
          const m = parseInt(from.slice(2, 4), 10) || 0;
          const lessonStart = new Date(lessonDate.getFullYear(), lessonDate.getMonth(), lessonDate.getDate(), h, m, 0, 0);
          return lessonStart.getTime() >= nowDate.getTime();
        })
        .sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          if (dateA.toDateString() !== dateB.toDateString()) return dateA.getTime() - dateB.getTime();
          const timeA = (a.timeFrom || a.from || '00:00').toString().replace(/:/g, '').slice(0, 4);
          const timeB = (b.timeFrom || b.from || '00:00').toString().replace(/:/g, '').slice(0, 4);
          return (timeA || 0) - (timeB || 0);
        })
        .slice(0, UPCOMING_EVENTS_LIMIT);

      setTodayEvents(upcoming);
      homeEventsCache.events = upcoming;
      homeEventsCache.lastFetch = Date.now();
    } catch (error) {
      console.error('Error loading upcoming events:', error);
      setTodayEvents([]);
    } finally {
      if (!silent) setClassesLoading(false);
    }
  };

  // Функция для расчета дохода из расписания напрямую из Supabase (оптимизированная)
  const calculateMonthlyIncome = async (forceRefresh = false) => {
    try {
      // Проверяем кэш в sessionStorage
      const cacheKey = 'monthlyIncomeCache';
      const cacheExpiry = 30 * 1000; // 30 секунд (меньше времени периодического обновления)
      
      // Очищаем кэш при принудительном обновлении
      if (forceRefresh) {
        sessionStorage.removeItem(cacheKey);
      } else {
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          try {
            const { data, timestamp } = JSON.parse(cachedData);
            const cacheAge = Date.now() - timestamp;
            if (cacheAge < cacheExpiry) {
              // Используем кэш только если он свежий
              setMonthlyEarnings(data.monthlyEarnings);
              setPlannedIncome(data.plannedIncome);
              return;
            }
          } catch (e) {
            // Если кэш поврежден, удаляем его
            sessionStorage.removeItem(cacheKey);
          }
        }
      }

      // Получаем текущего пользователя и teacher_id
      const user = await getCurrentUser();
      if (!user || user.role !== 'teacher') {
        console.warn('User not authenticated or not a teacher');
        setMonthlyEarnings(0);
        setPlannedIncome(0);
        return;
      }

      // Кэшируем teacher_id, чтобы не запрашивать его каждый раз
      if (!teacherIdRef.current) {
        const { data: teacherRecord, error: teacherError } = await supabase
          .from('teachers')
          .select('id')
          .eq('auth_user_id', user.id)
          .single();

        if (teacherError || !teacherRecord) {
          console.error('Teacher not found:', teacherError);
          setMonthlyEarnings(0);
          setPlannedIncome(0);
          return;
        }

        teacherIdRef.current = teacherRecord.id;
      }

      // Получаем текущий месяц и год
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1; // Месяц от 1 до 12
      
      // Формируем даты начала и конца месяца в формате YYYY-MM-DD
      const firstDayOfMonth = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
      const lastDayOfMonth = new Date(currentYear, currentMonth, 0).getDate();
      const lastDayOfMonthStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(lastDayOfMonth).padStart(2, '0')}`;

      // Оптимизированный запрос: получаем расписание и данные студентов одним запросом через JOIN
      const { data: scheduleItems, error: scheduleError } = await supabase
        .from('schedule')
        .select(`
          id,
          date,
          time_from,
          time_to,
          status,
          student_id,
          students!inner(id, name, price)
        `)
        .eq('teacher_id', teacherIdRef.current)
        .eq('type', 'lesson')
        .gte('date', firstDayOfMonth)
        .lte('date', lastDayOfMonthStr)
        .not('student_id', 'is', null);

      if (scheduleError) {
        console.error('Error fetching schedule:', scheduleError);
        setMonthlyEarnings(0);
        setPlannedIncome(0);
        return;
      }

      let realIncome = 0; // Реальный доход - только done
      let plannedIncomeValue = 0; // Планируемый доход - все кроме cancelled

      if (scheduleItems && scheduleItems.length > 0) {
        scheduleItems.forEach(item => {
          // Пропускаем если нет студента
          if (!item.student_id || !item.students) return;

          // Получаем данные студента из JOIN результата
          const student = item.students;

          // Парсим цену из строки типа "$40/h" или "40"
          const priceStr = student.price || '';
          const priceMatch = priceStr.match(/(\d+(?:\.\d+)?)/);
          const pricePerHour = priceMatch ? parseFloat(priceMatch[1]) : 0;

          // Рассчитываем длительность урока в часах
          let durationHours = 1; // По умолчанию 1 час
          const fromTime = item.time_from;
          const toTime = item.time_to;

          if (fromTime && toTime) {
            const fromParts = fromTime.split(':');
            const toParts = toTime.split(':');
            const fromMinutes = parseInt(fromParts[0] || 0) * 60 + parseInt(fromParts[1] || 0);
            const toMinutes = parseInt(toParts[0] || 0) * 60 + parseInt(toParts[1] || 0);
            const durationMinutes = toMinutes - fromMinutes;
            if (durationMinutes > 0) {
              durationHours = Math.max(0.5, durationMinutes / 60); // Минимум 0.5 часа
            }
          }

          const lessonValue = pricePerHour * durationHours;

          // Реальный доход (Money this month) - только завершенные уроки со статусом 'done'
          if (item.status === 'done') {
            realIncome += lessonValue;
          }

          // Планируемый доход (Planned income) - все уроки текущего месяца кроме отмененных
          if (item.status !== 'cancelled') {
            plannedIncomeValue += lessonValue;
          }
        });
      }

      const roundedRealIncome = Math.round(realIncome);
      const roundedPlannedIncome = Math.round(plannedIncomeValue);

      // Сохраняем в кэш
      sessionStorage.setItem(cacheKey, JSON.stringify({
        data: {
          monthlyEarnings: roundedRealIncome,
          plannedIncome: roundedPlannedIncome
        },
        timestamp: Date.now()
      }));

      setMonthlyEarnings(roundedRealIncome);
      setPlannedIncome(roundedPlannedIncome);
    } catch (error) {
      console.error('Error calculating monthly income:', error);
      setMonthlyEarnings(0);
      setPlannedIncome(0);
    }
  };

  // Обновляем refs при каждом рендере для доступа к актуальным версиям функций
  useEffect(() => {
    calculateMonthlyIncomeRef.current = calculateMonthlyIncome;
    calculateLessonsCountRef.current = calculateLessonsCount;
    loadUpcomingEventsRef.current = loadUpcomingEvents;
  });

  // Load events on component mount: использовать кэш если свежий
  useEffect(() => {
    loadUpcomingEvents(false, true);
    calculateMonthlyIncome();
    calculateStudentsCount();
    calculateLessonsCount();
  }, []);

  // Periodic refresh every 30 seconds — без показа loading, чтобы не мигало
  useEffect(() => {
    const interval = setInterval(() => {
      loadUpcomingEvents(true);
      calculateMonthlyIncome();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Refresh when window gains focus — без показа loading
  useEffect(() => {
    const handleFocus = () => {
      loadUpcomingEvents(true);
      calculateMonthlyIncome();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Refetch Classes при возврате на вкладку (visibility), если кэш устарел
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - homeEventsCache.lastFetch >= HOME_EVENTS_CACHE_TTL_MS) {
        loadUpcomingEvents(true, false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Обновляем данные при изменении расписания (только доход и уроки)
  useEffect(() => {
    const handleScheduleUpdate = (event) => {
      console.log('📅 scheduleUpdated event received:', event.detail);
      console.log('📅 Refs status:', {
        calculateMonthlyIncome: !!calculateMonthlyIncomeRef.current,
        calculateLessonsCount: !!calculateLessonsCountRef.current,
        loadUpcomingEvents: !!loadUpcomingEventsRef.current
      });
      
      // Используем refs для доступа к актуальным версиям функций
      if (calculateMonthlyIncomeRef.current) {
        console.log('💰 Updating monthly income...');
        calculateMonthlyIncomeRef.current(true).catch(err => console.error('Error updating income:', err));
      } else {
        console.warn('⚠️ calculateMonthlyIncomeRef.current is null');
      }
      
      if (calculateLessonsCountRef.current) {
        console.log('📚 Updating lessons count...');
        calculateLessonsCountRef.current(true).catch(err => console.error('Error updating lessons count:', err));
      } else {
        console.warn('⚠️ calculateLessonsCountRef.current is null');
      }
      
      if (loadUpcomingEventsRef.current) {
        console.log('📅 Updating upcoming events...');
        loadUpcomingEventsRef.current(true).catch(err => console.error('Error updating events:', err));
      } else {
        console.warn('⚠️ loadUpcomingEventsRef.current is null');
      }
    };

    console.log('👂 Registering scheduleUpdated event listener');
    window.addEventListener('scheduleUpdated', handleScheduleUpdate);
    // Status changes initiated inside StudentProfile may not always touch the
    // schedule table directly (e.g. lesson-only rows). Also recompute on
    // lessonStatusUpdated so the money widget stays in sync.
    window.addEventListener('lessonStatusUpdated', handleScheduleUpdate);
    return () => {
      console.log('👋 Removing scheduleUpdated event listener');
      window.removeEventListener('scheduleUpdated', handleScheduleUpdate);
      window.removeEventListener('lessonStatusUpdated', handleScheduleUpdate);
    };
  }, []);

  // Обновляем студентов только при их изменении (добавление, удаление, изменение статуса)
  useEffect(() => {
    const handleStudentsUpdate = () => {
      calculateStudentsCount(true);
    };

    window.addEventListener('studentsUpdated', handleStudentsUpdate);
    window.addEventListener('novas-students-updated', handleStudentsUpdate);
    return () => {
      window.removeEventListener('studentsUpdated', handleStudentsUpdate);
      window.removeEventListener('novas-students-updated', handleStudentsUpdate);
    };
  }, []);

  // Ensure banner video autoplays across browsers (especially iOS Safari)
  useEffect(() => {
    const videoEl = bannerVideoRef.current;
    if (!videoEl) return;
    try {
      videoEl.muted = true;
      videoEl.setAttribute('muted', '');
      videoEl.setAttribute('playsinline', '');
      const playPromise = videoEl.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(() => {
          // Silently ignore autoplay restrictions; video remains muted background
        });
      }
    } catch (e) {
      // no-op
    }
  }, []);

  // Форматировать время, убирая секунды (HH:mm:ss -> HH:mm)
  const formatTime = (timeString) => {
    if (!timeString) return '9:00';
    // Если время в формате HH:mm:ss, убираем секунды
    if (timeString.includes(':') && timeString.split(':').length === 3) {
      const [hours, minutes] = timeString.split(':');
      return `${hours}:${minutes}`;
    }
    return timeString;
    };

  const getStudentName = (studentId, groupId) => {
    if (groupId) {
      // Используем данные из состояния (загружены из Supabase)
      const group = groupsData && (groupsData[groupId] || groupsData[groupId.toString()] || groupsData[parseInt(groupId)]);
      if (group) return group.name;
      // Fallback на localStorage если не найдено
      const localGroups = getGroupsData();
      const localGroup = localGroups && (localGroups[groupId] || localGroups[groupId.toString()] || localGroups[parseInt(groupId)]);
      return localGroup ? localGroup.name : 'Unknown Group';
    }
    if (!studentId) {
      return 'Unknown Student';
    }
    // Используем данные из состояния (загружены из Supabase)
    // studentId может быть UUID или slug
    let student = null;
    
    // Пробуем найти по UUID (id)
    if (studentsData[studentId]) {
      student = studentsData[studentId];
    } else {
      // Пробуем найти по slug
      student = Object.values(studentsData).find(s => 
        s.id === studentId || 
        s.slug === studentId || 
        s.invite_slug === studentId ||
        s.studentId === studentId
      );
    }
    
    if (student) return student.name;
    
    // Fallback на localStorage если не найдено
    const localStudents = getStudentData();
    const localStudent = localStudents && (
      localStudents[studentId] || 
      localStudents[studentId.toString()] || 
      localStudents[parseInt(studentId)] ||
      Object.values(localStudents).find(s => 
        s.id === studentId || 
        s.slug === studentId ||
        s.studentId === studentId
      )
    );
    return localStudent ? localStudent.name : 'Unknown Student';
  };

  const getStudentDataById = (studentId, groupId) => {
    if (groupId) {
      const group = groupsData && (groupsData[groupId] || groupsData[groupId.toString()] || groupsData[parseInt(groupId)]);
      if (group) return group;
      const localGroups = getGroupsData();
      return localGroups && (localGroups[groupId] || localGroups[groupId.toString()] || localGroups[parseInt(groupId)]);
    }
    if (!studentId) return null;
    
    let student = null;
    if (studentsData[studentId]) {
      student = studentsData[studentId];
    } else {
      student = Object.values(studentsData).find(s => 
        s.id === studentId || 
        s.slug === studentId || 
        s.invite_slug === studentId ||
        s.studentId === studentId
      );
    }
    
    if (student) return student;
    
    const localStudents = getStudentData();
    return localStudents && (
      localStudents[studentId] || 
      localStudents[studentId.toString()] || 
      localStudents[parseInt(studentId)] ||
      Object.values(localStudents).find(s => 
        s.id === studentId || 
        s.slug === studentId ||
        s.studentId === studentId
      )
    );
  };

  // Функция для расчета количества студентов
  const calculateStudentsCount = async (forceRefresh = false) => {
    try {
      const cacheKey = 'studentsCountCache';
      const cacheExpiry = 5 * 60 * 1000; // 5 минут (как у дохода)
      
      if (forceRefresh) {
        sessionStorage.removeItem(cacheKey);
      } else {
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          try {
            const { data, timestamp } = JSON.parse(cachedData);
            if (Date.now() - timestamp < cacheExpiry) {
              setActiveStudentsCount(data.count);
              // Также обновляем studentsData из кэша если есть
              if (data.studentsData) {
                setStudentsData(data.studentsData);
              }
              return;
            }
          } catch (e) {
            sessionStorage.removeItem(cacheKey);
          }
        }
      }

      const students = await getStudentsFromSupabase();
      setStudentsData(students);
      const activeStudents = Object.values(students).filter(student => {
        return !student.status || student.status === 'Active' || student.status === 'active';
      });
      const count = activeStudents.length;
      
      sessionStorage.setItem(cacheKey, JSON.stringify({
        data: { count, studentsData: students },
        timestamp: Date.now()
      }));
      
      setActiveStudentsCount(count);
    } catch (error) {
      console.error('Error loading students data:', error);
      const students = getStudentData();
      setStudentsData(students);
      const activeStudents = Object.values(students).filter(student => {
        return !student.status || student.status === 'Active' || student.status === 'active';
      });
      setActiveStudentsCount(activeStudents.length);
    }
  };

  // Функция для расчета количества уроков на неделе и месяце
  const calculateLessonsCount = async (forceRefresh = false) => {
    try {
      const cacheKey = 'lessonsCountCache';
      const cacheExpiry = 30 * 1000; // 30 секунд
      
      if (forceRefresh) {
        sessionStorage.removeItem(cacheKey);
      } else {
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          try {
            const { data, timestamp } = JSON.parse(cachedData);
            if (Date.now() - timestamp < cacheExpiry) {
              setLessonsThisWeek(data.week);
              setLessonsThisMonth(data.month);
              return;
            }
          } catch (e) {
            sessionStorage.removeItem(cacheKey);
          }
        }
      }

      const schedule = await getSchedule();
      
      const savedLessons = getLessonsFromLocalStorage();
      const allLessons = [
        ...(schedule || []),
        ...savedLessons.filter(lesson => 
          !(schedule || []).some(scheduleLesson => scheduleLesson.id === lesson.id)
        )
      ];

      // Расчет недели
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay() + 1);
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      
      const weekLessons = allLessons.filter(lesson => {
        if (!lesson.date || lesson.type === 'event') return false;
        const lessonDate = new Date(lesson.date);
        return lessonDate >= startOfWeek && lessonDate <= endOfWeek;
      });
      
      const weekCount = weekLessons.reduce((count, lesson) => {
        const fromTime = lesson.from || lesson.timeFrom || lesson.time_from || '0:0';
        const toTime = lesson.to || lesson.timeTo || lesson.time_to || '1:0';
        const startTime = fromTime.split(':');
        const endTime = toTime.split(':');
        const startMinutes = parseInt(startTime[0] || 0) * 60 + parseInt(startTime[1] || 0);
        const endMinutes = parseInt(endTime[0] || 0) * 60 + parseInt(endTime[1] || 0);
        const durationMinutes = endMinutes - startMinutes;
        const durationHours = durationMinutes / 60;
        return count + Math.max(1, Math.round(durationHours));
      }, 0);

      // Расчет месяца
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();
      
      const monthLessons = allLessons.filter(lesson => {
        if (!lesson.date || lesson.type === 'event') return false;
        const lessonDate = new Date(lesson.date);
        return lessonDate.getMonth() === currentMonth && lessonDate.getFullYear() === currentYear;
      });
      
      const monthCount = monthLessons.reduce((count, lesson) => {
        const fromTime = lesson.from || lesson.timeFrom || lesson.time_from || '0:0';
        const toTime = lesson.to || lesson.timeTo || lesson.time_to || '1:0';
        const startTime = fromTime.split(':');
        const endTime = toTime.split(':');
        const startMinutes = parseInt(startTime[0] || 0) * 60 + parseInt(startTime[1] || 0);
        const endMinutes = parseInt(endTime[0] || 0) * 60 + parseInt(endTime[1] || 0);
        const durationMinutes = endMinutes - startMinutes;
        const durationHours = durationMinutes / 60;
        return count + Math.max(1, Math.round(durationHours));
      }, 0);

      sessionStorage.setItem(cacheKey, JSON.stringify({
        data: { week: weekCount, month: monthCount, scheduleData: schedule || [] },
        timestamp: Date.now()
      }));

      setLessonsThisWeek(weekCount);
      setLessonsThisMonth(monthCount);
    } catch (error) {
      console.error('Error calculating lessons count:', error);
      setLessonsThisWeek(0);
      setLessonsThisMonth(0);
    }
  };

  // State for students count
  const [activeStudentsCount, setActiveStudentsCount] = useState(0);
  
  // State for students and groups data (for getStudentName)
  const [studentsData, setStudentsData] = useState({});
  const [groupsData, setGroupsData] = useState({});
  
  // State for lessons counts
  const [lessonsThisWeek, setLessonsThisWeek] = useState(0);
  const [lessonsThisMonth, setLessonsThisMonth] = useState(0);

  // Load groups data (только один раз при монтировании)
  useEffect(() => {
    const loadGroupsData = async () => {
      try {
        const groups = await getGroups();
        setGroupsData(groups);
      } catch (error) {
        console.error('Error loading groups data:', error);
        const groups = getGroupsData();
        setGroupsData(groups || {});
      }
    };

    loadGroupsData();
  }, []);

  // Quick Actions handlers
  const handleAddLesson = () => {
    const students = getStudentData();
    const studentNames = Object.values(students).map(student => student.name);
    
    if (studentNames.length === 0) {
      alert('No students found. Please add a student first.');
      return;
    }
    
    setShowStudentModal(true);
  };

  const handleStudentSelect = async (studentId, studentName) => {
    setShowStudentModal(false);
    if (pendingTemplate) {
      const tpl = pendingTemplate;
      setPendingTemplate(null);
      setTemplateLoading(tpl.id);
      try {
        const created = await saveLesson({
          id: 'new',
          title: tpl.title,
          studentId,
          containers: [{ id: 1, type: 'editor' }],
          editorContents: [{ id: 1, content: tpl.content }],
          status: 'planned',
          type: 'lesson',
        });
        navigate(`/lessons/${created.id}`);
      } catch (err) {
        console.error('Failed to create lesson from template:', err);
      } finally {
        setTemplateLoading(null);
      }
    } else {
      navigate(`/lessons/new?studentId=${studentId}&studentName=${encodeURIComponent(studentName)}`);
    }
  };

  const handleUseTemplate = (template) => {
    setPendingTemplate(template);
    setShowStudentModal(true);
  };

  const handleAddStudent = () => {
    navigate('/students/new');
  };

  const handleOpenSchedule = () => {
    navigate('/schedule');
  };

  // eslint-disable-next-line no-unused-vars
  const handleStatusChange = async (eventId, currentStatus) => {
    try {
      // Находим событие в текущем списке событий
      const event = todayEvents.find(e => e.id === eventId);
      if (!event) {
        console.warn('Event not found:', eventId);
        return;
      }

      // Циклически переключаем статусы: in_progress -> done -> cancelled -> in_progress
      let newStatus;
      switch (currentStatus) {
        case 'in_progress':
          newStatus = 'done';
          break;
        case 'done':
          newStatus = 'cancelled';
          break;
        case 'cancelled':
          newStatus = 'in_progress';
          break;
        default:
          newStatus = 'in_progress';
      }

      // Обновляем событие в Supabase
      const updatedEvent = {
        ...event,
        status: newStatus
      };

      await saveScheduleItem(updatedEvent);
      setTodayEvents(prev => prev.map(e => e.id === eventId ? { ...e, status: newStatus } : e));
      homeEventsCache.events = homeEventsCache.events.map(e => e.id === eventId ? { ...e, status: newStatus } : e);
    } catch (e) {
      console.error('Error updating lesson status:', e);
    }
  };

  const getStatusSlug = (status) => {
    const s = status || 'in_progress';
    if (s === 'in_progress') return 'in-progress';
    if (s === 'done') return 'completed';
    if (s === 'cancelled') return 'cancelled';
    return 'upcoming';
  };

  const getStatusDisplayName = (status) => {
    switch (status) {
      case 'in_progress':
        return 'in progress';
      case 'done':
        return 'completed';
      case 'cancelled':
        return 'cancelled';
      default:
        return 'in progress';
    }
  };

  // Закрытие выбора статуса по клику вне
  useEffect(() => {
    function handleClickOutside(e) {
      if (statusWrapRef.current && !statusWrapRef.current.contains(e.target)) setOpenStatusMenuId(null);
    }
    if (openStatusMenuId !== null) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openStatusMenuId]);

  const handleStatusMenuSelect = async (eventId, newStatus) => {
    try {
      // Находим событие в текущем списке событий
      const event = todayEvents.find(e => e.id === eventId);
      if (!event) {
        console.warn('Event not found:', eventId);
        return;
      }

      // Обновляем событие в Supabase через saveScheduleItem
      const updatedEvent = {
        ...event,
        status: newStatus
      };

      await saveScheduleItem(updatedEvent);
      
      // Также обновляем в localStorage для совместимости (novas_lessons_data
      // — единый кэш уроков)
      const allLessonsData = getLessonsFromLocalStorage();
      const updatedLessonsData = allLessonsData.map(lesson =>
        lesson.id === eventId ? { ...lesson, status: newStatus } : lesson
      );
      saveLessonsToLocalStorage(updatedLessonsData);

      // Если это урок (не событие), также обновляем в таблице lessons, если он там есть
      if (event.type === 'lesson' && event.id && event.id !== 'new') {
        try {
          const { saveLesson } = await import('../utils/lessonsService');
          // Проверяем, есть ли урок в таблице lessons (поиск по id или связанным данным)
          const allLessons = updatedLessonsData;
          const lessonInLessons = allLessons.find(l => l.id === eventId);
          
          if (lessonInLessons && (lessonInLessons.content || lessonInLessons.whiteboards || lessonInLessons.exercises)) {
            // Это полноценный урок - обновляем в Supabase lessons
            const updatedLesson = {
              ...lessonInLessons,
              status: newStatus
            };
            await saveLesson(updatedLesson);
          }
        } catch (error) {
          // Если урок не найден в lessons, это нормально - он может быть только в schedule
          console.log('Lesson not in lessons table, only in schedule:', eventId);
        }
      }

      // Оптимистичное обновление UI; полная перезагрузка произойдёт по scheduleUpdated из saveScheduleItem
      setTodayEvents(prev => prev.map(e => e.id === eventId ? { ...e, status: newStatus } : e));
      homeEventsCache.events = homeEventsCache.events.map(e => e.id === eventId ? { ...e, status: newStatus } : e);
      window.dispatchEvent(new CustomEvent('lessonStatusUpdated', {
        detail: { lessonId: eventId, status: newStatus, prevStatus: event.status }
      }));
      setOpenStatusMenuId(null);
    } catch (e) {
      console.error('Error updating lesson status:', e);
    }
  };

  return (
    <div className="home-container">
      {/* Banner Section */}
      <div className="home-banner-container">
        <video
          ref={bannerVideoRef}
          className="home-banner-video"
          src={process.env.PUBLIC_URL + '/banner-video.mp4'}
          autoPlay
          loop
          muted
          defaultMuted
          playsInline
          preload="auto"
        />
        <div className="home-banner-overlay" />
        
        {/* Statistics Bar at Top */}
        <div className="banner-stats-bar">
          <div className="banner-stat" data-tour="stat-students">
            <span className="banner-stat-value">{activeStudentsCount}</span>
            <span className="banner-stat-label">Students</span>
          </div>
          <div className="banner-stat-divider"></div>
          <div className="banner-stat" data-tour="stat-lessons-week">
            <span className="banner-stat-value">{lessonsThisWeek}</span>
            <span className="banner-stat-label">Lessons this week</span>
          </div>
          <div className="banner-stat-divider"></div>
          <div className="banner-stat" data-tour="stat-lessons-month">
            <span className="banner-stat-value">{lessonsThisMonth}</span>
            <span className="banner-stat-label">Lessons this month</span>
          </div>
          <div className="banner-stat-divider"></div>
          <div className="banner-stat" data-tour="stat-money">
            <span className="banner-stat-value">${monthlyEarnings.toLocaleString()}</span>
            <span className="banner-stat-label">Money this month</span>
          </div>
          <div className="banner-stat-divider"></div>
          <div className="banner-stat" data-tour="stat-planned">
            <span className="banner-stat-value">${plannedIncome.toLocaleString()}</span>
            <span className="banner-stat-label">Planned income</span>
          </div>
        </div>
        
        <div className="home-banner-content">
          <div className="magic-search-wrapper">
            <div className="magic-search-border">
              <input
                className="magic-search-input"
                type="text"
                placeholder="What will you teach today?"
                onFocus={() => {
                  setMagicFeatureToast(true);
                  if (magicToastTimeoutRef.current) clearTimeout(magicToastTimeoutRef.current);
                  magicToastTimeoutRef.current = setTimeout(() => {
                    setMagicFeatureToast(false);
                    magicToastTimeoutRef.current = null;
                  }, 4500);
                }}
                onKeyDown={() => {
                  if (!magicFeatureToast) {
                    setMagicFeatureToast(true);
                    if (magicToastTimeoutRef.current) clearTimeout(magicToastTimeoutRef.current);
                    magicToastTimeoutRef.current = setTimeout(() => {
                      setMagicFeatureToast(false);
                      magicToastTimeoutRef.current = null;
                    }, 4500);
                  }
                }}
              />
              <span className="magic-search-icon">✨</span>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Section */}
      <div className="dashboard-section">
        {/* Main 2-column grid: Classes | What's New */}
        <div className="home-main-grid">
          {/* Classes Section */}
          <div className="classes-container">
            <h2 className="card-title">Classes</h2>
            <div className="dashboard-card today-card">
              {classesLoading && (
                <div className="classes-loading" role="status" aria-label="Loading lessons">
                  <div className="classes-loading-bar" aria-hidden />
                </div>
              )}
              <div className="today-items">
              {classesLoading ? null : todayEvents.length > 0 ? (
                todayEvents.map(event => {
                  const studentName = event.type === 'event' ? event.title : getStudentName(event.studentId, event.groupId);
                  const timeText = event.allDay ? 'All day' : `${formatTime(event.from || event.timeFrom || '9:00')} – ${formatTime(event.to || event.timeTo || '10:00')}`;
                  const statusSlug = getStatusSlug(event.status);
                  const eventDate = event.date ? new Date(event.date) : null;
                  const today = new Date();
                  const dateLabel = !eventDate ? '' : eventDate.toDateString() === today.toDateString() ? 'Today' : (() => {
                    const tomorrow = new Date(today);
                    tomorrow.setDate(today.getDate() + 1);
                    return eventDate.toDateString() === tomorrow.toDateString() ? 'Tomorrow' : eventDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                  })();
                  return (
                    <div key={event.id} className="today-item">
                      <div className="home-schedule-card-top">
                        <div className="home-schedule-card-left">
                          <span className="home-schedule-time">{timeText}</span>
                          <span className="home-schedule-divider" aria-hidden />
                          {dateLabel && <span className="home-schedule-date">{dateLabel}</span>}
                        </div>
                        <div
                          ref={openStatusMenuId === event.id ? statusWrapRef : undefined}
                          className="home-schedule-status-wrap"
                        >
                          {event.type !== 'event' ? (
                            openStatusMenuId === event.id ? (
                              <div className="home-schedule-status-expanded" onClick={e => e.stopPropagation()}>
                                <div className="home-schedule-status-dots">
                                  {[
                                    { value: 'in_progress', color: 'orange', title: 'In progress' },
                                    { value: 'done', color: 'green', title: 'Done' },
                                    { value: 'cancelled', color: 'grey', title: 'Cancelled' },
                                ].map(({ value, color, title }) => (
                                  <button
                                    key={value}
                                    type="button"
                                    className={`home-schedule-status-dot home-schedule-status-dot--${color}${(event.status || 'in_progress') === value ? ' active' : ''}`}
                                    onClick={e => { e.stopPropagation(); handleStatusMenuSelect(event.id, value); }}
                                  >
                                    <span className="home-schedule-status-dot-tooltip">{title}</span>
                                  </button>
                                ))}
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className={`home-schedule-status home-schedule-status--${statusSlug} home-schedule-status-clickable home-schedule-status-single`}
                                onClick={e => { e.stopPropagation(); setOpenStatusMenuId(event.id); }}
                              >
                                <span className={`home-schedule-status-icon home-schedule-status-icon--${statusSlug}`} aria-hidden />
                                <span className="home-schedule-status-label">{getStatusDisplayName(event.status || 'in_progress')}</span>
                              </button>
                            )
                          ) : (
                            <span className={`home-schedule-status home-schedule-status--${statusSlug}`}>
                              <span className={`home-schedule-status-icon home-schedule-status-icon--${statusSlug}`} aria-hidden />
                              Event
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="home-schedule-meta">
                        <span className="home-schedule-name">{studentName}</span>
                        {event.type !== 'event' && (
                          <div className="home-schedule-actions">
                            <button type="button" className="item-action-btn profile-btn" onClick={() => { if (event.studentId) { const s = getStudentDataById(event.studentId); navigate(`/students/${s?.slug || s?.id || event.studentId}`); } }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="7" r="4"/><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/></svg>
                              <span>Profile</span>
                            </button>
                            <button type="button" className="item-action-btn generate-btn" onClick={() => { if (event.studentId) navigate(`/lessons/new?studentId=${event.studentId}&studentName=${encodeURIComponent(studentName)}`); }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                              <span>Create lesson</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="today-empty">
                  <div className="empty-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                  </div>
                  <p>You have no lessons</p>
                  <small>Add lessons in the schedule to see them here</small>
                  <button type="button" className="today-empty-add-btn" onClick={handleOpenSchedule}>
                    Add lesson
                  </button>
                </div>
              )}
              </div>
            </div>
            {/* Action Buttons */}
            <div className="classes-action-buttons">
              <button className="classes-action-btn" onClick={handleAddLesson}>
                <div className="classes-btn-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </div>
                <span>Add Lesson</span>
              </button>
              <button className="classes-action-btn" onClick={handleAddStudent}>
                <div className="classes-btn-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <span>Add Student</span>
              </button>
              <button className="classes-action-btn" onClick={handleOpenSchedule}>
                <div className="classes-btn-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14,2 14,8 20,8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10,9 9,9 8,9"/>
                  </svg>
                </div>
                <span>Open Schedule</span>
              </button>
            </div>
          </div>

          {/* What's New Section */}
          <div className="news-section">
            <h2 className="card-title">What's New</h2>
            <div className="news-list">
              {NEWS_ITEMS.map(item => (
                <div key={item.id} className="news-item">
                  <div className="news-item-left">
                    <span className="news-tag" style={{ background: item.tagColor }}>{item.tag}</span>
                  </div>
                  <div className="news-item-body">
                    <div className="news-item-title">{item.title}</div>
                    <div className="news-item-text">{item.text}</div>
                  </div>
                  <div className="news-item-date">{item.date}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Template Lessons Section */}
        <div className="template-lessons-section">
          <h2 className="card-title">Ready-to-Use Lessons</h2>
          <p className="template-lessons-subtitle">Pick a template, choose a student, and start teaching right away.</p>
          <div className="template-lessons-grid">
            {TEMPLATE_LESSONS.map(tpl => (
              <div key={tpl.id} className="template-card" style={{ background: tpl.gradient }}>
                <div className="template-card-glow" style={{ background: tpl.accentColor }} />
                <div className="template-card-top">
                  <div className="template-card-meta">
                    <span className="template-tag">{tpl.tag}</span>
                    <span className="template-level">{tpl.level}</span>
                  </div>
                  <span className="template-card-icon">{tpl.icon}</span>
                </div>
                <h3 className="template-card-title">{tpl.title}</h3>
                <p className="template-card-desc">{tpl.description}</p>
                <div className="template-card-exercises">
                  {tpl.exerciseTypes.map(ex => (
                    <span key={ex} className="template-exercise-pill">{ex}</span>
                  ))}
                </div>
                <button
                  className="template-use-btn"
                  onClick={() => handleUseTemplate(tpl)}
                  disabled={templateLoading === tpl.id}
                >
                  {templateLoading === tpl.id ? 'Creating…' : 'Use this lesson →'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Student Selection Modal */}
      {showStudentModal && (
        <div 
          className="student-modal-overlay" 
          onClick={() => setShowStudentModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)',
          }}
        >
          <div 
            className="student-modal" 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '16px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              width: '90%',
              maxWidth: '450px',
              maxHeight: 'calc(100vh - 80px)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div 
              className="student-modal-header"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '20px 20px 12px 20px',
                borderBottom: '1px solid #e5e7eb',
                flexShrink: 0,
              }}
            >
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>Select a Student</h3>
              <button 
                className="student-modal-close"
                onClick={() => setShowStudentModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  borderRadius: '8px',
                  width: '28px',
                  height: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#6b7280',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div 
              className="student-modal-content"
              style={{
                padding: '12px 20px 20px 20px',
                maxHeight: 'calc(70vh - 80px)',
                overflowY: 'auto',
                overflowX: 'hidden',
                flex: 1,
              }}
            >
              {Object.entries(getStudentData()).map(([studentId, student]) => (
                <div 
                  key={studentId}
                  className="student-option"
                  onClick={() => handleStudentSelect(studentId, student.name)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    marginBottom: '6px',
                    border: '2px solid transparent',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f8fafc';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  <div 
                    className="student-avatar"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #8a2be2 0%, #6b21a8 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px',
                      color: 'white',
                      flexShrink: 0,
                    }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </div>
                  <div 
                    className="student-modal-info"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      position: 'static',
                      background: 'transparent',
                      backdropFilter: 'none',
                      WebkitBackdropFilter: 'none',
                      height: 'auto',
                      padding: 0,
                      zIndex: 'auto',
                    }}
                  >
                    <h4 style={{ 
                      margin: '0 0 2px 0', 
                      fontSize: '15px', 
                      fontWeight: 600, 
                      color: '#111827',
                      whiteSpace: 'normal',
                      overflow: 'visible',
                      wordBreak: 'break-word',
                    }}>
                      {student.name}
                    </h4>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '13px', 
                      color: '#6b7280',
                      whiteSpace: 'normal',
                      overflow: 'visible',
                      wordBreak: 'break-word',
                    }}>
                      {student.proficiency}
                    </p>
                  </div>
                  <div 
                    className="student-status"
                    style={{
                      marginLeft: '12px',
                      flexShrink: 0,
                    }}
                  >
                    <span 
                      className={`status-badge ${student.status?.toLowerCase() || 'active'}`}
                      style={{
                        display: 'inline-block',
                        padding: '3px 6px',
                        borderRadius: '5px',
                        fontSize: '11px',
                        fontWeight: 500,
                        textTransform: 'capitalize',
                        background: student.status?.toLowerCase() === 'active' ? '#dcfce7' : 
                                   student.status?.toLowerCase() === 'inactive' ? '#fef2f2' :
                                   student.status?.toLowerCase() === 'on-vacation' ? '#fef3c7' :
                                   student.status?.toLowerCase() === 'paused' ? '#f3e8ff' : '#dcfce7',
                        color: student.status?.toLowerCase() === 'active' ? '#166534' : 
                               student.status?.toLowerCase() === 'inactive' ? '#dc2626' :
                               student.status?.toLowerCase() === 'on-vacation' ? '#92400e' :
                               student.status?.toLowerCase() === 'paused' ? '#7c3aed' : '#166534',
                      }}
                    >
                      {student.status || 'Active'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {magicFeatureToast && (
        <div className="magic-feature-toast" role="status" aria-live="polite">
          <div className="magic-feature-toast-sparkles" aria-hidden>
            <span className="magic-feature-toast-sparkle" />
          </div>
          <div className="magic-feature-toast-inner">
            <span className="magic-feature-toast-icon" aria-hidden>✨</span>
            <p className="magic-feature-toast-message">This magic feature is under development now</p>
            <button type="button" className="magic-feature-toast-close" onClick={() => setMagicFeatureToast(false)} aria-label="Close">×</button>
          </div>
        </div>
      )}

      {/* Onboarding tour + floating Quick Tour trigger */}
      <TourTrigger
        onClick={() => setTourOpen(true)}
        pulsing={!tourOpen && (() => { try { return !localStorage.getItem(HOME_TOUR_SEEN_KEY); } catch (_) { return false; } })()}
        tall
      />
      <Tour
        steps={HOME_TOUR_STEPS}
        isOpen={tourOpen}
        onClose={() => {
          setTourOpen(false);
          try { localStorage.setItem(HOME_TOUR_SEEN_KEY, '1'); } catch (_) {}
        }}
        onComplete={() => {
          try { localStorage.setItem(HOME_TOUR_SEEN_KEY, '1'); } catch (_) {}
          navigate('/students/new');
        }}
      />
    </div>
  );
}

